import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Upload, Download } from 'lucide-react';
import { Label } from '../ui/label';

interface ImportModalProps<T = any> {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (response: any) => void;
  onImport: (data: T[]) => Promise<any>;
  title?: string;
  sampleTemplateUrl?: string;
  parseRow?: (row: any) => T;
}

export const ImportModal = <T extends any = any>({
  isOpen,
  onClose,
  onSuccess,
  onImport,
  title = 'Import Users',
  sampleTemplateUrl = '/import-sample-excel/ImportusersSampleFile.xlsx',
  parseRow,
}: ImportModalProps<T>) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleDownloadSample = () => {
    try {
      const link = document.createElement('a');
      link.href = sampleTemplateUrl;
      const urlParts = sampleTemplateUrl.split('/');
      link.download = urlParts[urlParts.length - 1] || 'sample.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError('Failed to download sample template');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonDataRaw = XLSX.utils.sheet_to_json(worksheet);
          const jsonData = parseRow ? jsonDataRaw.map(parseRow) : jsonDataRaw;

          const response = await onImport(jsonData as T[]);
          onSuccess(response);
          onClose();
          setFile(null);
        } catch (err) {
          setError('Failed to process file');
        } finally {
          setLoading(false);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      setError('Failed to upload file');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Upload Excel File</Label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">
                Drop your Excel file here or click to browse
              </p>
              <input
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <Button
                className="mt-4"
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                <Upload className="mr-2 h-4 w-4" />
                Choose File
              </Button>
              {file && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected file: {file.name}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handleDownloadSample}
              className="text-blue-600 hover:text-blue-800"
            >
              <Download className="mr-2 h-4 w-4" />
              Download Sample Template
            </Button>
          </div>

          {error && (
            <div className="text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={loading}
            >
              {loading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 